/**
 * Two-column flex masonry: each column stacks its children at their natural
 * heights independently of the other column. Short widgets don't leave dead
 * space below them when the sibling on the other side is tall.
 *
 * Children are interleaved (even indices left, odd indices right) so the
 * order of <MasonryRow>{a}{b}{c}{d}</MasonryRow> reads top-to-bottom as
 * a, b, c, d the same way a 2-col grid would, but with no row gaps.
 *
 * On `breakpoint='md'` (default 'lg'), the layout collapses to a single
 * column for the small viewports below the breakpoint.
 */
import { Children, type ReactNode } from 'react';
import { clsx } from 'clsx';

interface MasonryRowProps {
  children: ReactNode;
  gap?: 'sm' | 'md' | 'lg';
  breakpoint?: 'md' | 'lg';
  className?: string;
}

const GAP: Record<NonNullable<MasonryRowProps['gap']>, string> = {
  sm: 'gap-3',
  md: 'gap-4',
  lg: 'gap-6',
};

export function MasonryRow({ children, gap = 'md', breakpoint = 'lg', className }: MasonryRowProps) {
  const all = Children.toArray(children).filter(Boolean);
  const left  = all.filter((_, i) => i % 2 === 0);
  const right = all.filter((_, i) => i % 2 === 1);
  const gridCols = breakpoint === 'md' ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 lg:grid-cols-2';
  return (
    <div className={clsx('grid', gridCols, GAP[gap], 'items-start', className)}>
      <div className={clsx('flex flex-col', GAP[gap])}>{left}</div>
      <div className={clsx('flex flex-col', GAP[gap])}>{right}</div>
    </div>
  );
}
